/**
 * ThemeColorSelector - Selettore colore tema
 * Permette di scegliere tra 6 colori predefiniti o un colore HEX personalizzato
 */
import React, { useState, useEffect } from 'react';
import { Check, Palette } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';

// 6 colori predefiniti
const PRESET_COLORS = [
  { name: 'Verde', hsl: '142 70% 45%', hex: '#22c55e', description: 'Default' },
  { name: 'Blu', hsl: '220 70% 50%', hex: '#3b82f6', description: 'Professionale' },
  { name: 'Viola', hsl: '262 83% 58%', hex: '#8b5cf6', description: 'Moderno' },
  { name: 'Arancione', hsl: '25 95% 53%', hex: '#f97316', description: 'Energico' },
  { name: 'Rosa', hsl: '330 80% 55%', hex: '#ec4899', description: 'Vivace' },
  { name: 'Teal', hsl: '175 70% 40%', hex: '#14b8a6', description: 'Fresco' },
];

// Converte HEX in HSL
const hexToHsl = (hex) => {
  // Rimuovi # se presente
  hex = hex.replace(/^#/, '');
  
  // Parse RGB
  let r = parseInt(hex.substring(0, 2), 16) / 255;
  let g = parseInt(hex.substring(2, 4), 16) / 255;
  let b = parseInt(hex.substring(4, 6), 16) / 255;
  
  let max = Math.max(r, g, b);
  let min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0;
  } else {
    let d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  
  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);
  
  return `${h} ${s}% ${l}%`;
};

// Applica il colore al documento
const applyColor = (hsl) => {
  document.documentElement.style.setProperty('--primary', hsl);
  document.documentElement.style.setProperty('--ring', hsl);
  document.documentElement.style.setProperty('--brand', hsl);
  
  // Calcola varianti light/dark
  const parts = hsl.split(' ');
  const h = parseInt(parts[0]);
  const s = parseInt(parts[1]);
  const l = parseInt(parts[2]);
  
  const lightL = Math.min(l + 15, 85);
  const darkL = Math.max(l - 15, 25);
  
  document.documentElement.style.setProperty('--brand-light', `${h} ${s}% ${lightL}%`);
  document.documentElement.style.setProperty('--brand-dark', `${h} ${s}% ${darkL}%`);
};

// Salva e carica preferenza
const saveColorPreference = (color) => {
  localStorage.setItem('theme-primary-color', JSON.stringify(color));
};

const loadColorPreference = () => {
  try {
    const saved = localStorage.getItem('theme-primary-color');
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

// Hook per inizializzare il colore al caricamento
export const useThemeColor = () => {
  useEffect(() => {
    const saved = loadColorPreference();
    if (saved?.hsl) {
      applyColor(saved.hsl);
    }
  }, []);
};

const ThemeColorSelector = () => {
  const [selectedColor, setSelectedColor] = useState(null);
  const [customHex, setCustomHex] = useState('');
  const [isValidHex, setIsValidHex] = useState(true);

  useEffect(() => {
    const saved = loadColorPreference();
    if (saved) {
      setSelectedColor(saved);
      if (saved.isCustom) {
        setCustomHex(saved.hex);
      }
    } else {
      setSelectedColor(PRESET_COLORS[0]);
    }
  }, []);

  const handleSelectPreset = (color) => {
    setSelectedColor(color);
    setCustomHex('');
    applyColor(color.hsl);
    saveColorPreference(color);
  };

  const handleCustomHexChange = (e) => {
    const value = e.target.value;
    setCustomHex(value);
    
    // Valida formato HEX
    const hexRegex = /^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    const isValid = hexRegex.test(value);
    setIsValidHex(isValid || value === '');
    
    if (isValid && value.length >= 3) {
      const hex = value.startsWith('#') ? value : `#${value}`;
      const hsl = hexToHsl(hex);
      const customColor = {
        name: 'Personalizzato',
        hsl,
        hex,
        isCustom: true
      };
      setSelectedColor(customColor);
      applyColor(hsl);
      saveColorPreference(customColor);
    }
  };

  return (
    <div className="space-y-4" data-testid="theme-color-selector">
      <div className="flex items-center gap-2 mb-2">
        <Palette className="h-4 w-4 text-muted-foreground" />
        <Label className="text-sm font-medium">Colore Tema</Label>
      </div>
      
      {/* Colori Predefiniti */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
        {PRESET_COLORS.map((color) => (
          <button
            key={color.name}
            onClick={() => handleSelectPreset(color)}
            className={`relative flex flex-col items-center p-3 rounded-lg border-2 transition-all hover:scale-105 ${
              selectedColor?.hex === color.hex && !selectedColor?.isCustom
                ? 'border-primary ring-2 ring-primary/20'
                : 'border-transparent hover:border-muted-foreground/30'
            }`}
            style={{ backgroundColor: `hsl(${color.hsl} / 0.1)` }}
            title={color.description}
            data-testid={`color-${color.name.toLowerCase()}`}
          >
            <div
              className="w-8 h-8 rounded-full shadow-md flex items-center justify-center"
              style={{ backgroundColor: color.hex }}
            >
              {selectedColor?.hex === color.hex && !selectedColor?.isCustom && (
                <Check className="h-4 w-4 text-white" />
              )}
            </div>
            <span className="text-xs mt-2 font-medium">{color.name}</span>
          </button>
        ))}
      </div>
      
      {/* Input HEX Personalizzato */}
      <div className="flex items-end gap-3 pt-2">
        <div className="flex-1">
          <Label htmlFor="custom-hex" className="text-xs text-muted-foreground">
            Colore personalizzato (HEX)
          </Label>
          <div className="flex items-center gap-2 mt-1">
            <div
              className="w-10 h-10 rounded-lg border shadow-inner shrink-0"
              style={{ 
                backgroundColor: isValidHex && customHex 
                  ? (customHex.startsWith('#') ? customHex : `#${customHex}`)
                  : '#e5e7eb'
              }}
            />
            <Input
              id="custom-hex"
              type="text"
              placeholder="#22c55e"
              value={customHex}
              onChange={handleCustomHexChange}
              className={`font-mono ${!isValidHex ? 'border-destructive' : ''}`}
              maxLength={7}
              data-testid="custom-hex-input"
            />
          </div>
          {!isValidHex && customHex && (
            <p className="text-xs text-destructive mt-1">Formato HEX non valido</p>
          )}
        </div>
      </div>
      
      {/* Anteprima */}
      {selectedColor && (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 mt-4">
          <div
            className="w-6 h-6 rounded-full"
            style={{ backgroundColor: selectedColor.hex }}
          />
          <div className="text-sm">
            <span className="font-medium">{selectedColor.name}</span>
            <span className="text-muted-foreground ml-2">{selectedColor.hex}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemeColorSelector;
